import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col } from "reactstrap";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { selectUser } from "store/features/auth/authSlice";
import { useUpdateClientLeadMutation } from "store/features/clientLeads/clientLeadsManagement";

const AddLeadSummaryModal = ({
  isOpen,
  toggle,
  lead,
  onLeadSummaryAdded
}) => {
  const user = useSelector(selectUser);
  const [updateClientLead, { isLoading }] = useUpdateClientLeadMutation();

  const [formData, setFormData] = useState({
    title: "",
    lead_summary: "",
    hours_spent: "",
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        title: "",
        lead_summary: "",
        hours_spent: "",
        date: new Date().toISOString().split('T')[0]
      });
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.lead_summary.trim()) {
      toast.error("Lead summary is required");
      return;
    }

    if (!formData.hours_spent || parseFloat(formData.hours_spent) <= 0) {
      toast.error("Please enter valid hours spent");
      return;
    }

    try {
      // Create new summary entry
      const newSummaryEntry = {
        id: Date.now(), // Temporary ID
        employer_id: user.id,
        employer_name: user.nickname || user.name,
        employer_role: user.role,
        company_name: lead?.company,
        date: formData.date,
        title: formData.title,
        lead_summary: formData.lead_summary,
        hours_spent: parseFloat(formData.hours_spent) || 0
      };

      // Get existing summaries and add new one
      const existingSummaries = lead?.summary || [];
      const updatedSummaries = [...existingSummaries, newSummaryEntry];

      // Update the lead with new summaries array
      await updateClientLead({
        id: lead.id,
        formData: { summary: updatedSummaries }
      }).unwrap();

      toast.success("Lead summary added successfully!");

      // Reset form and close modal
      setFormData({
        title: "",
        lead_summary: "",
        hours_spent: "",
        date: new Date().toISOString().split('T')[0]
      });

      toggle();
      if (onLeadSummaryAdded) {
        onLeadSummaryAdded();
      }
    } catch (error) {
      toast.error("Failed to add lead summary");
      console.error('Lead summary creation error:', error);
    }
  };

  const handleModalClose = () => {
    setFormData({
      title: "",
      lead_summary: "",
      hours_spent: "",
      date: new Date().toISOString().split('T')[0]
    });
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleModalClose} size="lg">
      <ModalHeader toggle={handleModalClose}>
        Add Lead Summary - {lead?.company}
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Lead Information */}
          <div className="mb-4 p-3 bg-light rounded">
            <h6 className="text-primary mb-2">Lead Information</h6>
            <div className="row">
              <div className="col-md-6">
                <small><strong>Company:</strong> {lead?.company}</small>
              </div>
              <div className="col-md-6">
                <small><strong>Contact:</strong> {lead?.name}</small>
              </div>
              <div className="col-md-6">
                <small><strong>Service:</strong> {lead?.service_name}</small>
              </div>
              <div className="col-md-6">
                <small><strong>Marketing Person:</strong> {user.nickname || user.name}</small>
              </div>
            </div>
          </div>

          <Row>
            <Col lg={6}>
              <div className="mb-3">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  className="form-control"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <label className="form-label">Hours Spent *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="24"
                  className="form-control"
                  name="hours_spent"
                  value={formData.hours_spent}
                  onChange={handleChange}
                  placeholder="e.g., 2.5"
                  required
                />
                <small className="text-muted">Enter hours spent (0.5 for 30 minutes)</small>
              </div>
            </Col>
            {/* <Col lg={12}>
              <div className="mb-3">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter summary title (optional)"
                />
              </div>
            </Col> */}
            <Col lg={12}>
              <div className="mb-3">
                <label className="form-label">Lead Summary *</label>
                <textarea
                  className="form-control"
                  name="lead_summary"
                  value={formData.lead_summary}
                  onChange={handleChange}
                  rows="6"
                  placeholder={`Describe your interaction with ${lead?.company}. Include details about discussions, follow-ups, next steps, and any important information...`}
                  required
                />
                <small className="text-muted">
                  Provide detailed information about your lead management activities
                </small>
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isLoading}>
            {isLoading ? "Adding..." : "Add Lead Summary"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AddLeadSummaryModal;